import { useField } from "formik";
import { useState, useRef, useEffect } from "react";

const StudentSelectInput = ({
  label,
  data = [],
  onChange,
  labelClassName,
  onSearch,
  showLabel = true,
  ...props
}) => {
  const [field, meta] = useField(props);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedCityName, setSelectedCityName] = useState(""); // ✅ display city name
  const dropdownRef = useRef(null);
  const isError = meta.touched && meta.error;

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Debounced API call for search
  useEffect(() => {
    if (!isOpen || !onSearch) return;
    const trimmed = searchTerm.trim();
    if (trimmed === "") return;

    setIsTyping(true);
    const delayDebounce = setTimeout(() => {
      onSearch(trimmed);
      setIsTyping(false);
    }, 600);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // ✅ Local filtering when new data arrives
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
    } else {
      setFilteredData(
        data.filter((item) =>
          item.cityName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [data, searchTerm]);

  // ✅ Update displayed name when value (cityId) changes
  useEffect(() => {
    if (field.value && data.length > 0) {
      const selected = data.find((item) => item.cityId === field.value);
      if (selected) setSelectedCityName(selected.cityName);
    }
  }, [field.value, data]);

  const handleSelect = (item) => {
    const value = item.cityId;
    const name = item.cityName;

    // ✅ Store cityId in Formik
    if (props.form?.setFieldValue) {
      props.form.setFieldValue(props.name, value);
    } else {
      field.onChange({ target: { name: props.name, value } });
    }

    // ✅ Display cityName
    setSelectedCityName(name);

    if (onChange) onChange({ target: { value } });
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="flex flex-col">
      {showLabel && <label
        className={`p-1 mt-2 text-gray-700 flex gap-1 ${labelClassName || 'text-lg'}`}
        htmlFor={props.id || props.name}
      >
        {label}
         {isError && (
          <div className="text-red-500 text-[12px]">*</div>
        )}
      </label>}

      <div className="flex flex-col relative" ref={dropdownRef}>
        <div
          className={`p-2 text-gray-700 border rounded-lg cursor-pointer bg-white ${isError ? "" : "border-gray-200"
            }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span
            className="block w-full text-gray-600 text-[14px] truncate whitespace-nowrap overflow-hidden"
          >
            {selectedCityName || `${label}`}
          </span>
        </div>



        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {/* 🔍 Search Input */}
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
              <input
                type="text"
                autoFocus
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 placeholder-gray-400 placeholder:text-[14px]"
              />
            </div>

            {/* Options */}
            {filteredData.length > 0 ? (
              filteredData.map((item, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                  onClick={() => handleSelect(item)}
                >
                  {item.cityName}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No results found
              </div>
            )}
          </div>
        )}

       
      </div>
    </div>
  );
};

export default StudentSelectInput;
